/**
 * Composable pour les calculs de paiement
 * Gère les montants, frais Stripe/Donoor, et formatage
 */

interface PaymentCalculationsOptions {
  amount: Ref<string>;
  includeFees: Ref<boolean>;
  donoorFeeRate: Ref<number>; // Taux de commission Donoor (ex: 0.015 pour 1.5%)
  isSubscription: Ref<boolean>;
  subscriptionPeriod: Ref<string>;
  donateToUsAmount?: Ref<number>; // Montant du don à Donoor (optionnel)
  // ─── Estimation des frais PSP par provider (Karim 2026-08-15) ───
  // Le donateur qui paie en PayPal supporte un frais différent de Stripe. Ces
  // options rendent l'estimation « couvrir les frais » provider-aware. TOUTES
  // OPTIONNELLES → si absentes, on reste EXACTEMENT sur le taux Stripe
  // (comportement historique strictement inchangé).
  paymentProvider?: Ref<"stripe" | "paypal">; // provider effectif du paiement
  paypalFeePercent?: Ref<number>; // taux PayPal appris EN POURCENT (ex: 2.9)
  paypalFeeFixed?: Ref<number>; // part fixe PayPal en € (ex: 0.25)
  /**
   * Reproduire EXACTEMENT le calcul du serveur (commission arrondie au
   * centime, total arrondi au plus proche) au lieu de l'arrondi au supérieur
   * historique.
   *
   * 🔴 POURQUOI (Karim 2026-09-23, audit). Sur l'ADHÉSION et la BILLETTERIE,
   * c'est le SERVEUR qui fixe le montant débité (`calculateFeesForMembership`
   * / `calculateFeesForTicketing`, arrondi au plus proche). Cette page
   * affichait son propre calcul (arrondi au supérieur, commission non
   * arrondie) : dans ~50 % des cas, 1 centime d'écart entre le montant
   * affiché — et validé dans la feuille Apple Pay — et le montant débité.
   * Stripe exige que les deux soient identiques.
   *
   * Laissé à `false` pour le DON : là, le serveur débite le montant envoyé
   * par la page, les deux sont donc déjà identiques — rien à changer.
   * Les deux arrondis laissent l'association entière (0 écart sur 85 632 cas
   * simulés).
   */
  arrondiServeur?: boolean;
  /**
   * DON À L'ASSOCIATION laissé en plus des billets (billetterie, migration
   * 290), en euros. Il s'ajoute au montant débité et à l'assiette des frais du
   * prestataire, mais JAMAIS à la commission Donoor — exactement comme le
   * serveur (`calculateFeesForTicketing`, paramètre `donationEuros`). Le
   * passer dans `amount` le faisait commissionner, et le montant affiché
   * divergeait du montant débité.
   */
  donAssociation?: Ref<number>;
}

export const usePaymentCalculations = (options: PaymentCalculationsOptions) => {
  const { t } = useI18n();
  const { formatCurrency } = useFormatLangue();
  const {
    amount,
    includeFees,
    donoorFeeRate,
    isSubscription,
    subscriptionPeriod,
    donateToUsAmount,
    paymentProvider,
    paypalFeePercent,
    paypalFeeFixed,
    arrondiServeur = false,
    donAssociation,
  } = options;

  /** Don à l'association (0 hors billetterie). */
  const don = computed(() => Math.max(0, Number(donAssociation?.value) || 0));

  const arrondi2 = (n: number) => Math.round(n * 100) / 100;

  /**
   * Total débité, frais couverts — UNE formule, deux arrondis :
   *  - `arrondiServeur` : miroir exact du serveur (adhésion, billetterie) ;
   *  - sinon : arrondi au supérieur historique (don).
   */
  function totalFraisCouverts(net: number, pourboire: number): number {
    const commission = arrondiServeur
      ? arrondi2(net * donoorFeeRate.value)
      : net * donoorFeeRate.value;
    // Même ordre d'opérations que le serveur (net + don + commission +
    // pourboire + fixe) : sans don, `net + 0` est exactement `net`, aucun
    // montant existant ne bouge.
    const brut =
      (net + don.value + commission + pourboire + pspFixed.value) /
      (1 - pspRate.value);
    return arrondiServeur ? arrondi2(brut) : Math.ceil(brut * 100) / 100;
  }

  // Constantes de frais Stripe (taux standard France pour cartes européennes)
  const STRIPE_RATE = 0.015; // 1.5%
  const STRIPE_FIXED_FEE = 0.25; // 0.25€

  // Taux/fixe PSP EFFECTIFS selon le provider choisi. PayPal → taux appris
  // (converti en décimal) ; sinon (Stripe ou options absentes) → taux Stripe
  // historique. Garde-fous : valeurs PayPal invalides → repli sur Stripe.
  const pspRate = computed<number>(() => {
    if (paymentProvider?.value === "paypal") {
      const pct = paypalFeePercent?.value;
      if (typeof pct === "number" && pct > 0) return pct / 100;
    }
    return STRIPE_RATE;
  });
  const pspFixed = computed<number>(() => {
    if (paymentProvider?.value === "paypal") {
      const fixed = paypalFeeFixed?.value;
      if (typeof fixed === "number" && fixed >= 0) return fixed;
    }
    return STRIPE_FIXED_FEE;
  });

  /**
   * Montant final sélectionné (avant frais)
   */
  const finalAmount = computed(() => {
    return parseFloat(amount.value) || 0;
  });

  /**
   * Montant avec frais inclus (si l'utilisateur a coché l'option)
   *
   * Calcule le montant total à payer pour que l'organisation reçoive le montant final
   * après déduction des frais Stripe (1.5% + 0.25€) et des frais Donoor.
   *
   * IMPORTANT: Les frais Donoor sont calculés sur le montant NET (ce que l'organisation reçoit),
   * pas sur le montant brut. Cela correspond à la logique du backend.
   *
   * Formule: X = (amount * (1 + donoor_rate) + donateToUs + stripe_fixed) / (1 - stripe_rate)
   * où: amount = montant net pour l'organisation
   *     donoor_rate = taux de commission Donoor (ex: 0.0395 pour 3.95%)
   *     donateToUs = don optionnel à Donoor
   *     stripe_fixed = 0.25€
   *     stripe_rate = 1.5%
   */
  const amountWithFees = computed(() => {
    const amount = finalAmount.value; // Montant que l'organisation doit recevoir (NET)
    const donateToUs = donateToUsAmount?.value || 0;
    if (amount === 0 && donateToUs === 0 && don.value === 0) return 0;

    // Si les frais ne sont pas inclus, retourner seulement le montant (+ don
    // à l'association) + don à Donoor — même ordre que le serveur.
    if (!includeFees.value) {
      return amount + don.value + donateToUs;
    }

    // Formule unifiée (net + commission + soutien + fixe PSP) / (1 − taux PSP),
    // arrondie selon `arrondiServeur` (cf. totalFraisCouverts).
    return totalFraisCouverts(amount, donateToUs);
  });

  /**
   * Frais Donoor uniquement (calculés sur le montant NET)
   */
  const donoorFeesAmount = computed(() => {
    const amount = finalAmount.value;
    if (amount === 0) return 0;
    return Math.round(amount * donoorFeeRate.value * 100) / 100;
  });

  /**
   * Frais Stripe uniquement (1.5% + 0.25€ sur le total)
   */
  const stripeFeesAmount = computed(() => {
    const amount = finalAmount.value;
    const donateToUs = donateToUsAmount?.value || 0;
    if (amount === 0 && donateToUs === 0 && don.value === 0) return 0;
    const donoorFees = donoorFeesAmount.value;

    // Frais PSP: calculés sur le montant total après Donoor
    // Total = (base + fixed) / (1 - rate), donc frais PSP = Total - base
    const baseAmount = amount + don.value + donoorFees + donateToUs;
    if (arrondiServeur) {
      // Déduits du total RÉELLEMENT débité : la ligne « frais » + les autres
      // lignes retombent exactement sur le total affiché.
      return Math.max(0, arrondi2(totalFraisCouverts(amount, donateToUs) - baseAmount));
    }
    const totalWithPsp = (baseAmount + pspFixed.value) / (1 - pspRate.value);
    const pspFees = totalWithPsp - baseAmount;

    return Math.round(pspFees * 100) / 100;
  });

  /**
   * Montant des frais (différence entre total et montant net)
   *
   * Calcule le total des frais (Stripe + Donoor) que le donateur paie en plus.
   * N'inclut PAS le don à Donoor (qui est un choix volontaire, pas des frais).
   * Affiche toujours les frais, même si la case n'est pas cochée, pour informer l'utilisateur.
   *
   * IMPORTANT: Utilise la même formule que amountWithFees pour garantir la cohérence.
   */
  const feesAmount = computed(() => {
    const amount = finalAmount.value;
    const donateToUs = donateToUsAmount?.value || 0;
    if (amount === 0 && donateToUs === 0 && don.value === 0) return 0;
    // Même formule que amountWithFees (totalFraisCouverts). Les frais sont
    // la différence, moins le soutien Donoor et le don à l'association (des
    // choix, pas des frais).
    return totalFraisCouverts(amount, donateToUs) - amount - don.value - donateToUs;
  });

  /**
   * Montant final avec réduction (si applicable)
   * Pour l'instant pas de réduction, mais prêt pour l'avenir
   * ✅ Utilise toujours amountWithFees qui inclut maintenant donateToUsAmount
   */
  const finalAmountWithDiscount = computed(() => {
    return amountWithFees.value;
  });

  /**
   * Formater un montant en devise
   */
  const formatAmount = (amount: number): string => formatCurrency(amount);

  /**
   * Label de période pour l'affichage
   */
  const periodLabel = computed(() => {
    if (!isSubscription.value) return "";

    switch (subscriptionPeriod.value) {
      case "weekly":
        return `/${t("don.periodes.semaine")}`;
      case "monthly":
        return `/${t("don.periodes.mois")}`;
      case "yearly":
        return `/${t("don.periodes.an")}`;
      default:
        return "";
    }
  });

  /**
   * Label de période complet (traduit)
   */
  const periodLabelFull = computed(() => {
    if (!isSubscription.value) return "";

    switch (subscriptionPeriod.value) {
      case "weekly":
        return t("don.periodes.hebdomadaire");
      case "monthly":
        return t("don.periodes.mensuel");
      case "yearly":
        return t("don.periodes.annuel");
      default:
        return "";
    }
  });

  /**
   * Intervalle Stripe (pour l'API)
   */
  const stripeInterval = computed(() => {
    switch (subscriptionPeriod.value) {
      case "weekly":
        return "week";
      case "monthly":
        return "month";
      case "yearly":
        return "year";
      default:
        return "month";
    }
  });

  return {
    // Montants calculés
    finalAmount,
    amountWithFees,
    feesAmount,
    finalAmountWithDiscount,
    donoorFeesAmount,
    stripeFeesAmount,

    // Formatage
    formatAmount,
    periodLabel,
    periodLabelFull,
    stripeInterval,

    // Constantes exposées (au cas où)
    STRIPE_RATE,
    STRIPE_FIXED_FEE,
  };
};
